import {
  GGOBaseComponent,
  IconComponent,
  Message,
  Progress,
  Translate,
} from '@galigeo-store/ui-lib';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PortalAppContext } from '../../../state/portal-appcontext.js';
import './divider.js';
import { GaligeoCloudService } from '../../../services/galigeo-cloud.service';
import { publish, PUBSUB_TOPICS, subscribe } from '../../../state/pubsub.js';
import { isAdminUser } from '../../../services/user-service.js';


@customElement('panel-footer')
export class FooterComponent extends GGOBaseComponent {
  @property({ type: Boolean }) panelOpen = false;
  @property({ type: Object }) element: any = {};
  @property({ type: String }) active: boolean = false;
  @property({ type: Boolean, reflect: true }) profileOpen = false;
  @property({ type: Boolean }) languageOpen = false;
  @property({ type: String }) activeElement = '';

  static get styles() {
    return [
      css`
        :host {
          display: block;
          padding: 6.6px;
          transform: translateY(0);
          transition: transform 0.5s ease-in-out;
          margin-bottom: 20px;
        }
        .item {
          border-radius: 0.5rem;
          color: #fff;
          border: solid 1px #e5e5e5;
          font-weight: 500;
        }
        .language-item,
        .language-item.active {
          color: #03234d !important;
          background-color: transparent !important;
          fill: #03234d !important;
        }
        .profile-card .icon {
          width: 30px;
          height: 30px;
          padding: 6px;
          flex-shrink: 0;
        }
        .expanded-item .icon {
          width: 16px;
          height: 16px;
          padding: 12px;
          flex-shrink: 0;
        }
        .label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .label.hidden {
          display: none; /* Hide label when the panel is closed */
        }
        .item.active {
          background-color: #fff;
          color: #03234d;
          fill: #03234d;
        }
        .item.active svg {
          fill: currentColor;
        }
        .profile-label {
          font-size: 12px;
          font-weight: 500;
          color: #b0b0b0;
        }
        .profile-card,
        .expanded-item,
        .expanded-item-container {
          display: flex;
          align-items: center;
          cursor: pointer;
        }
        .expanded-item-container {
          justify-content: space-between;
        }
        .expanded-container {
          padding: 0.5rem;
        }
        .sub {
          overflow: hidden;
          max-height: 0;
          opacity: 0;
          transition:
            max-height 0.5s ease,
            opacity 0.5s ease;
        }
        .sub.active {
          max-height: 500px;
          opacity: 1;
        }
        .divider {
          margin: 0 1rem;
          border: 1px solid #e5e5e5;
        }
        .admin-item .icon {
          width: 20px;
          height: 20px;
          padding: 10px;
          flex-shrink: 0;
          fill: #fff;
        }
        .admin-item {
          display: flex;
          align-items: center;
          border-radius: 6px;
          color: #fff;
          border: solid 1px transparent;
          font-weight: 500;
          margin-bottom: 1rem;
          cursor: pointer;
        }
        .admin-item:hover {
          border: solid 1px #fff;
        }
        .admin-item.active {
          box-shadow: inset 300px 0 0 0 #ffffff;
          color: #1b96ff;
          transition: background-color 0.2s all-ease;
        }
        .admin-item.active svg {
          fill: #1b96ff;
        }
        .items-container {
          display: flex;
          flex-direction: column;
        }
        .expanded-item {
          display: flex;
          align-items: center;
          cursor: pointer;
          flex-wrap: wrap;
        }
        .language-item .expanded-container {
          max-height: 120px;
          overflow-y: auto;
        }
        .language-item .expanded-item span {
          display: inline-block;
          min-width: 80px;
        }
        .language-option {
          margin: 0 0.5rem;
          cursor: pointer;
        }
        .language-item .expanded-item ggo-icon {
          margin-left: auto;
        }
      `,
    ];
  }

  constructor() {
    super();
    subscribe(PUBSUB_TOPICS.REFRESH_LEFT_PANEL, () => {
      this.requestUpdate();
    });
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('panelOpen') && !this.panelOpen) {
      this.profileOpen = false;
      this.languageOpen = false;
    }
  }

  async toggleProfile() {
    this.profileOpen = !this.profileOpen;
  }

  async togglePortal() {
    const ok = await Message.confirm(Translate.get('footer_portal_toggle'));
    if (ok) {
      Progress.start(Translate.get('footer_portal_toggle_progress'));
      const result = await GaligeoCloudService.togglePortal(false);
      Progress.stop();
      if (result?.url) {
        window.location.href = result.url;
      } else throw new Error(Translate.get('footer_portal_toggle_error'));
    }
  }

  logout() {
    console.log('Logout clicked');
    GaligeoCloudService.logoutUser();
  }
  private readonly languages = [
    { label: 'Français', value: 'fr' },
    { label: 'English', value: 'en' },
    { label: 'Deutsch', value: 'de' },
    { label: 'Español', value: 'es' },
    { label: 'Italiano', value: 'it' },
    { label: 'Português', value: 'pt' },
    { label: 'Nederlands', value: 'nl' },
  ];

  @property({ type: String }) selectedLanguage = Translate.getLanguage();

  private async handleLanguageSelect(lang: string) {
    this.selectedLanguage = lang;

    publish(PUBSUB_TOPICS.LANGUAGE_CHANGED, { lang });
  }

  private toggleLanguage() {
    this.languageOpen = !this.languageOpen;
  }

  render() {
    const user = PortalAppContext.getCurrentUser();

    const isAdmin = isAdminUser();

    return html`
      <div class="items-container">
        <div
          class="admin-item ${this.activeElement === 'admin' ? 'active' : ''}"
          style="${isAdmin ? '' : 'display:none'}"
          @click=${() => {
        publish(PUBSUB_TOPICS.GO_TO_ADMIN);
      }}
        >
          <svg
            class="icon"
            xmlns="http://www.w3.org/2000/svg"
            alt="icon"
            preserveAspectRatio="xMidYMid meet"
          >
            <use href="./svg/icons.svg#admin" />
          </svg>
          <span class="label ${this.panelOpen ? '' : 'hidden'}"
            >${Translate.get('administration')}</span
          >
        </div>
        <div class="item ${this.profileOpen ? 'active' : ''}">
          <div class="sub ${this.profileOpen ? 'active' : ''}">
            <div class="expanded-container">
              <div class="expanded-item-container" @click="${this.togglePortal}">
                <div class="expanded-item">
                  <svg
                    class="icon"
                    xmlns="http://www.w3.org/2000/svg"
                    alt="icon"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <use href="./svg/icons.svg#classic" />
                  </svg>
                  <span>${Translate.get('old_portal')}</span>
                </div>
              </div>
              <!-- Bloc langue comme un item indépendant -->
              <div class="language-item ${this.languageOpen ? 'active' : ''}">
                <div class="expanded-item" @click=${this.toggleLanguage}>
                  <img
                    src="/svg/language-svgrepo-com.svg"
                    alt="Language"
                    style="width:16px; height:16px; padding: 12px;"
                  />
                  <span class="label">${Translate.get('Language')}</span>
                  <ggo-icon
                    icon="chevrondown"
                    size="${IconComponent.XXSMALL}"
                  ></ggo-icon>
                </div>
                <div class="sub ${this.languageOpen ? 'active' : ''}">
                  <div class="expanded-container">
                    <div class="expanded-item">
                      ${this.languages.map(
        (lang) => html`
                          <span
                            class="language-option"
                            style="font-weight: ${this.selectedLanguage ===
            lang.value
            ? 'bold'
            : 'normal'}"
                            @click=${() =>
            this.handleLanguageSelect(lang.value)}
                            title="${lang.label}"
                          >
                            ${lang.label}
                          </span>
                        `,
      )}
                    </div>
                  </div>
                </div>
              </div>
              <div class="expanded-item" @click=${() => this.logout()}>
                <svg
                  class="icon"
                  xmlns="http://www.w3.org/2000/svg"
                  alt="icon"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <use href="./svg/icons.svg#logout" />
                </svg>
                <span>${Translate.get('logout')}</span>
              </div>
            </div>
            <div class="divider"></div>
          </div>
          <div class="profile-card" @click=${this.toggleProfile}>
            <svg
              class="icon"
              xmlns="http://www.w3.org/2000/svg"
              alt="icon"
              preserveAspectRatio="xMidYMid meet"
            >
              <use href="./svg/icons.svg#user" />
            </svg>
            <span class="label ${this.panelOpen ? '' : 'hidden'}">
              <div class="user-label">${user.user}</div>
              <div class="profile-label">${user.orgId}</div>
            </span>
          </div>
        </div>
      </div>
    `;
  }
}
